import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import CompositeSoil from "./compositeSoilForm";
import "./AgriculturalWizardForms.css";

function FieldSoilForm(props) {
  const { t } = useTranslation();
  const [isStandardSoil, setSoilType] = useState(props.isStandard);
  const [currentDepthLevel] = useState(0);
  const [depthLevel, setLevel] = useState(props.depth);
  const [otherInfo, setOtherInfo] = useState({
    type: props.SoilProp,
    field_uid: props.fieldId || "",
  });

  const listSoils = Array.isArray(props.listSoils) ? props.listSoils : [];
  const fields = Array.isArray(props.fields) ? props.fields : [];

  useEffect(() => {
    setOtherInfo((previous) => ({
      ...previous,
      field_uid: props.fieldId || "",
    }));
  }, [props.fieldId]);

  useEffect(() => {
    setOtherInfo((previous) => ({ ...previous, type: props.SoilProp }));
    setSoilType(props.isStandard);
  }, [props.SoilProp, props.isStandard]);

  const handleSoilPick = (event) => {
    const selectedSoil = listSoils.find(
      (soil) => soil.soil === event.target.value
    );

    if (!selectedSoil) return;

    props.handleSoilType?.(selectedSoil.soil);
    props.handleSoilTypeId?.(selectedSoil.id);
    props.handleRuMax?.(parseFloat(selectedSoil.ru));
    props.handleEffRain?.(parseFloat(selectedSoil.rain_eff));
    props.handleRuPractical?.(
      parseFloat(selectedSoil.practical_fraction)
    );
  };

  const handleFieldChange = (event) => {
    const selectedFieldId = event.target.value;
    const selectedField = fields.find(
      (field) => field.Uid === selectedFieldId
    );

    setOtherInfo((previous) => ({
      ...previous,
      field_uid: selectedFieldId,
    }));
    props.handleFieldChange?.(
      selectedFieldId,
      selectedField?.title || ""
    );
  };

  const handlePropertyChange = (event) => {
    const type = event.target.value;
    setOtherInfo((previous) => ({ ...previous, type }));
    props.handleSoilProprety?.(type);
    setSoilType(type === "Standard");
  };

  const compositeSoil = !isStandardSoil ? (
    <CompositeSoil
      key={currentDepthLevel + 1}
      depth={depthLevel[currentDepthLevel]?.depth}
      clay={depthLevel[currentDepthLevel]?.clay}
      sand={depthLevel[currentDepthLevel]?.sand}
      silt={depthLevel[currentDepthLevel]?.silt}
      OM={depthLevel[currentDepthLevel]?.OM}
      pH={depthLevel[currentDepthLevel]?.pH}
      Ecd={depthLevel[currentDepthLevel]?.Ecd}
      CEC={depthLevel[currentDepthLevel]?.CEC}
      soilProprety={otherInfo.type}
      soilType={props.soilType}
      onChange={(value) => {
        const newDepthLevel = [...depthLevel];
        newDepthLevel[currentDepthLevel] = value;
        setLevel(newDepthLevel);
      }}
    />
  ) : null;

  return (
    <Col lg="12" className="sf-agri-form sf-agri-soil-form">
      <Row className="sf-agri-form-section g-3">
        <Col lg="4" md="6" xs="12">
          <Form.Group controlId="soil-zone-name">
            <Form.Label>{t("soil_zone")} *</Form.Label>
            <Form.Control
              value={props.zoneName || ""}
              placeholder={t("soil_zone")}
              required
              onChange={props.handleZoneName}
            />
          </Form.Group>

          <Form.Group controlId="soil-type">
            <Form.Label>{t("soil_type")} *</Form.Label>
            <Form.Select
              value={props.soilType || ""}
              onChange={handleSoilPick}
              required
            >
              <option value="">{t("select_soil")}</option>
              {listSoils.map((soil) => (
                <option key={soil.id || soil.soil} value={soil.soil}>
                  {soil.soil}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group controlId="soil-property">
            <Form.Label>{t("soil_prop")}</Form.Label>
            <Form.Select value={otherInfo.type} onChange={handlePropertyChange}>
              <option value="Standard">Standard</option>
              <option value="Composite">Composite</option>
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="soil-field">
            <Form.Label>{t("name_field")} *</Form.Label>
            {props.fieldName ? (
              <Form.Control value={props.fieldName} disabled readOnly />
            ) : (
              <Form.Select
                value={otherInfo.field_uid}
                onChange={handleFieldChange}
                required
              >
                <option value="">{t("select_field")}</option>
                {fields.map((field) => (
                  <option key={field.Uid} value={field.Uid}>
                    {field.title}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
        </Col>
      </Row>

      {compositeSoil && (
        <Row className="sf-agri-form-section sf-agri-composite-section g-3">
          {compositeSoil}
        </Row>
      )}

      <Row className="sf-agri-form-section g-3">
        <Col lg="4" md="6" xs="12">
          <Form.Group controlId="irrigArea">
            <Form.Label>{t("surface_irriguée")} (ha)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={props.irrigArea ?? ""}
              onChange={(event) =>
                props.handleIrrigArea?.(event.target.value)
              }
              placeholder={t("surface_irriguée")}
            />
          </Form.Group>
        </Col>
        <Col lg="4" md="6" xs="12">
          <Form.Group controlId="effPluie">
            <Form.Label>{t("efficacité_pluie")} (%)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              max="100"
              value={props.effPluie ?? ""}
              onChange={(event) => props.handleEffRain?.(event.target.value)}
              placeholder={t("efficacité_pluie")}
            />
          </Form.Group>
        </Col>
        <Col lg="4" md="6" xs="12">
          <Form.Group controlId="ruMax">
            <Form.Label>RU max (mm/m)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={props.RUmax ?? ""}
              onChange={(event) => props.handleRuMax?.(event.target.value)}
              placeholder="RU max"
            />
          </Form.Group>
        </Col>
      </Row>
    </Col>
  );
}

FieldSoilForm.defaultProps = {
  isStandard: true,
  SoilProp: "Standard",
  depth: [
    {
      depth: 10,
      clay: 20,
      sand: 60,
      silt: 20,
      CEC: 1,
      OM: 1,
      pH: 2,
      Ecd: 1,
      uni: "Clay",
      soilProprety: "Standard",
    },
  ],
  listSoils: [],
  fields: [],
  isWizardMode: false,
  showCarousel: false,
};

export default FieldSoilForm;
